import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { installSessionClient, SESSION_MARKER } from '@/lib/sessionClient';

installSessionClient();

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // The real token is in an HttpOnly cookie; `token` is only a non-secret "signed in" marker for older screens.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialCheckDone = useRef(false);

  const clearLocal = useCallback(() => {
    sessionStorage.removeItem('fbc_signed_in');
    setToken(null);
    setUser(null);
  }, []);

  const markSignedIn = useCallback((u) => {
    sessionStorage.setItem('fbc_signed_in', '1');
    setToken(SESSION_MARKER);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    clearLocal();
  }, [clearLocal]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
        return res.data;
      } catch (err) {
        clearLocal();
      }
    }
  }, [token, clearLocal]);

  // Initial auth check - runs once on mount
  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;

    const checkAuth = async () => {
      // OAuth callback in progress — MainApp exchanges session_id first via completeGoogleLogin
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      sessionStorage.removeItem('fbc_token'); // old versions kept the raw token here
      try {
        const res = await axios.get(`${API}/auth/me`, { timeout: 8000 });
        markSignedIn(res.data);
      } catch (err) {
        clearLocal();
      }
      // Small delay to prevent flash
      await new Promise(resolve => setTimeout(resolve, 50));
      setLoading(false);
    };

    checkAuth();
  }, [markSignedIn, clearLocal]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    return markSignedIn(res.data.user);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Exchange the OAuth session_id (from the URL fragment) for an FBC session
  const completeGoogleLogin = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/auth/google-session`, { session_id: sessionId });
    return markSignedIn(res.data.user);
  }, [markSignedIn]);

  const register = async (userData) => {
    // Support both old signature and new object-based registration
    let payload;
    if (typeof userData === 'object' && userData.email) {
      // New object-based registration with all fields
      payload = {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        role: userData.role || 'buyer',
        phone: userData.phone || null,
        project_id: userData.project_id || null,
        flat_number: userData.flat_number || null,
        // Professional fields
        occupation: userData.occupation || null,
        qualification: userData.qualification || null,
        professional_reg_number: userData.professional_reg_number || null,
        organization: userData.organization || null,
        designation: userData.designation || null,
        years_of_experience: userData.years_of_experience || null,
        areas_of_expertise: userData.areas_of_expertise || null,
        linkedin_url: userData.linkedin_url || null,
        ethos_ack: !!userData.ethos_ack,
      };
    } else {
      // Legacy signature: register(email, password, name, role, phone, projectId, flatNumber)
      const [email, password, name, role, phone, projectId, flatNumber] = arguments;
      payload = { 
        email, password, name, 
        role: role || 'buyer', 
        phone: phone || null,
        project_id: projectId || null,
        flat_number: flatNumber || null
      };
    }
    
    const res = await axios.post(`${API}/auth/register`, payload);
    return markSignedIn(res.data.user);
  };

  const isAdmin = user?.role === 'admin';
  const isDeveloper = user?.role === 'developer';
  const isBuyer = user?.role === 'buyer';
  const isProfessional = user?.role === 'professional';
  const isBuyerGroupAdmin = user?.role === 'buyer_group_admin';
  const isVerified = user?.verification_status === 'verified';
  const isPending = user?.verification_status === 'pending';

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, logout, register, refreshUser,
      loginWithGoogle, completeGoogleLogin,
      isAdmin, isDeveloper, isBuyer, isProfessional, isBuyerGroupAdmin, isVerified, isPending 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
